/**
 * #653 — the words a Recent row shows. Pure (no React), so the rules are unit-tested.
 */
import type { RecentWorkGroup } from '@papan/files/client'
import { LOCATION_SEPARATOR, nameOwnSpace, readableName } from '../locations'

export type RecentDaySection = 'today' | 'yesterday' | 'thisWeek' | 'earlier'
export const RECENT_DAY_SECTIONS: readonly RecentDaySection[] = ['today', 'yesterday', 'thisWeek', 'earlier']

/** Local midnight of the day `ms` falls in, in the viewer's own time zone. */
function startOfDay(ms: number): number {
  const d = new Date(ms)
  d.setHours(0, 0, 0, 0)
  return d.getTime()
}

/**
 * Which section a change falls in, in the viewer's time zone: Today, Yesterday, earlier this week
 * (up to six days back — a weekday name there never repeats today's), or Earlier. Day boundaries are local midnights, so a DST change
 * never moves a change into the wrong day.
 */
export function daySectionOf(iso: string, now: Date = new Date()): RecentDaySection {
  const at = Date.parse(iso)
  if (!Number.isFinite(at)) return 'earlier'
  const today = startOfDay(now.getTime())
  if (at >= today) return 'today'
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)
  if (at >= yesterday.getTime()) return 'yesterday'
  // Six days back, not seven: a weekday name in "earlier this week" never repeats today's.
  const weekAgo = new Date(today)
  weekAgo.setDate(weekAgo.getDate() - 6)
  if (at >= weekAgo.getTime()) return 'thisWeek'
  return 'earlier'
}

/** Groups in their day sections, newest first, empty sections left out. */
export function groupByDay<G extends { latestAt: string }>(groups: readonly G[], now: Date = new Date()): Array<{ section: RecentDaySection; groups: G[] }> {
  const by = new Map<RecentDaySection, G[]>()
  for (const g of groups) {
    const s = daySectionOf(g.latestAt, now)
    const list = by.get(s)
    if (list) list.push(g)
    else by.set(s, [g])
  }
  return RECENT_DAY_SECTIONS.filter((s) => by.has(s)).map((section) => ({ section, groups: by.get(section)! }))
}

export interface RecentWords {
  /** i18n, bound to the `files` namespace. */
  t: (key: string, opts?: Record<string, unknown>) => string
  /** The viewer's identity in the tenant (their own space reads "My Space"). */
  viewerEmail: string | null | undefined
  /** The drive's own name for its top ("Acme's Drive"). */
  rootLabel: string
}

/** A single-file group reads as that file; anything else is a group. */
export function isSingleFile(g: RecentWorkGroup): boolean {
  return g.kind !== 'app' && g.fileCount === 1 && g.files.length === 1
}

/**
 * A path as a person reads it: the viewer's own `Persons/<email>` is "My Space" (one crumb — never
 * "Persons › My Space"), as the sidebar and the pickers name it; every other name readable.
 */
function readablePath(path: readonly { name: string }[], w: RecentWords): string[] {
  const mySpaceLabel = w.t('driveHome.nav.mySpace')
  const named = nameOwnSpace(path, { viewerEmail: w.viewerEmail, mySpaceLabel })
  // Only the second crumb is ever renamed, and only under Persons.
  const own = path.length > 1 && named[1].name === mySpaceLabel && path[1].name !== mySpaceLabel
  return (own ? named.slice(1) : named).map((c) => (own && c === named[1] ? c.name : readableName(c.name)))
}

/** The row's main line: the app, the file, or "12 files changed in Sales". */
export function groupTitle(g: RecentWorkGroup, w: RecentWords): string {
  if (g.kind === 'app') return readableName(g.place?.name ?? '')
  if (isSingleFile(g)) return g.files[0].name
  // The place's name as its path reads it (the viewer's own space is "My Space").
  const place = g.place ? (readablePath(g.path, w).at(-1) ?? readableName(g.place.name)) : w.rootLabel
  return w.t('driveHome.recent.filesChanged', { count: g.fileCount, place })
}

/** Who, as the row says it: You / a name / a guest / the app. */
export function actorName(g: RecentWorkGroup, w: RecentWords): string {
  const a = g.actor
  if (a.kind === 'person') return a.isYou ? w.t('driveHome.recent.you') : (a.name || w.t('driveHome.recent.someone'))
  if (a.kind === 'guest') return w.t('driveHome.recent.guest')
  // An app the viewer can't open comes without its name.
  return a.name ? readableName(a.name) : w.t('driveHome.recent.anApp')
}

/** "You edited" / "Alice uploaded" / "You edited with AI" — what happened, by whom. */
export function reasonLine(g: RecentWorkGroup, w: RecentWords): string {
  const who = actorName(g, w)
  if (g.withAI) return w.t('driveHome.recent.reason.ai', { who })
  const allUploaded = g.files.length > 0 && g.files.every((f) => f.change === 'uploaded')
  return w.t(allUploaded ? 'driveHome.recent.reason.uploaded' : 'driveHome.recent.reason.edited', { who })
}

/**
 * Where the row's work is, as a person reads it ("My Space › Projects"). A group names its place in
 * its title, so its location stops ABOVE the place; a single file's location is its whole folder path.
 * Top of the drive → the drive's own name.
 */
export function locationText(g: RecentWorkGroup, w: RecentWords): string {
  // The whole path read as a person reads it, then — for a group, which names its place in its title — without
  // the place (so "My Space" itself is a title, and its location the drive).
  const words = readablePath(g.path, w)
  const shown = isSingleFile(g) ? words : words.slice(0, -1)
  return shown.length === 0 ? w.rootLabel : shown.join(LOCATION_SEPARATOR)
}

const timeOfDay = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' })
const dayAndTime = new Intl.DateTimeFormat(undefined, { weekday: 'short', hour: 'numeric', minute: '2-digit' })

/**
 * A row's time, told apart from its neighbours' — and readable without a section heading (Home's list has
 * none): today "5 minutes ago"; yesterday "Yesterday, 9:05 AM"; earlier this week the day and time ("Fri 2:20
 * PM") — two pieces of work the same day would otherwise both read "4 days ago"; older, the date.
 */
export function recentTimeLabel(iso: string, now: Date = new Date(), relative: (iso: string) => string, t?: RecentWords['t']): string {
  const section = daySectionOf(iso, now)
  if (section === 'today') return relative(iso)
  const at = new Date(iso)
  if (section === 'yesterday') return t ? t('driveHome.recent.yesterdayAt', { time: timeOfDay.format(at) }) : timeOfDay.format(at)
  if (section === 'thisWeek') return dayAndTime.format(at)
  return relative(iso)
}

/** Groups flattened to their files, newest first (the chat home panel, the phone's Recent strip). An app has none. */
export function flattenRecentFiles(groups: readonly RecentWorkGroup[], max: number, wordsOf?: Pick<RecentWords, 't' | 'viewerEmail'>): Array<RecentWorkGroup['files'][number] & { placeName: string | null }> {
  const out: Array<RecentWorkGroup['files'][number] & { placeName: string | null }> = []
  const seen = new Set<string>()
  for (const g of groups) {
    for (const f of g.files) {
      if (seen.has(f.id)) continue
      seen.add(f.id)
      // The folder the file is in (the viewer's own space is "My Space").
      const folder = f.folder
      const isOwnSpace = !!folder && g.path.length > 1 && g.path[1].id === folder.id && g.path[0].name === 'Persons'
        && !!wordsOf && folder.name.toLowerCase() === (wordsOf.viewerEmail ?? '').toLowerCase()
      out.push({ ...f, placeName: folder ? (isOwnSpace ? wordsOf!.t('driveHome.nav.mySpace') : readableName(folder.name)) : null })
    }
  }
  return out.sort((a, b) => b.at.localeCompare(a.at)).slice(0, max)
}
